import React, { useCallback, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { AutocompleteCheckList } from 'components/FilterComponents/AutocompleteCheckList';
import { cn } from 'components/lib/utils';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  UIButton
} from '../../../../../../../components/ui';
import { ERROR_FIELD_CLASS } from '../../../../constants';

export interface ContentItemsSelectProps {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  renderValue?: (selected: string[]) => string;
  hasError?: boolean;
  onClose?: () => void;
}

export const ContentItemsSelect: React.FC<ContentItemsSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Search and select items',
  renderValue,
  hasError,
  onClose
}) => {
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) onClose?.();
    },
    [onClose]
  );

  const displayText = useMemo(() => {
    if (!value?.length) return placeholder;
    if (renderValue) return renderValue(value);
    if (value.length === options.length) return 'All';
    if (value.length === 1) {
      return options.find((o) => o.value === value[0])?.label ?? value[0];
    }
    return value
      .map((id) => options.find((o) => o.value === id)?.label ?? id)
      .join(', ');
  }, [value, placeholder, renderValue, options]);

  const handleToggle = useCallback(
    (toggledValue: string) => {
      const newSelection = value.includes(toggledValue)
        ? value.filter((v) => v !== toggledValue)
        : [...value, toggledValue];
      onChange(newSelection);
    },
    [value, onChange]
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <UIButton
          variant="outline"
          className={cn(
            'shadow-none group h-10 justify-between bg-white p-s text-base-foreground font-normal w-full hover:bg-base-accent focus:bg-base-accent',
            !value?.length && 'text-base-mutedForeground',
            hasError && ERROR_FIELD_CLASS
          )}
        >
          <div className="flex items-center justify-between w-full">
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">
              {displayText}
            </span>
            <FontAwesomeIcon
              icon="fa-solid fa-chevron-down"
              className="text-base-foreground transition-transform group-data-[state=open]:rotate-180 text-[#404040]"
            />
          </div>
        </UIButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-[--radix-popover-trigger-width] !p-0 rounded-md bg-white text-base-foreground font-normal z-1000"
      >
        <AutocompleteCheckList
          items={options}
          selectedItems={value}
          onChange={handleToggle}
          placeholder={placeholder}
          leftIcon={<FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />}
        />
      </PopoverContent>
    </Popover>
  );
};
